function defaultNow() {
  if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
    return performance.now();
  }
  return Date.now();
}

function eventKeys(event) {
  if (event?.keysChanged && typeof event.keysChanged[Symbol.iterator] === 'function') {
    return event.keysChanged;
  }
  if (event?.changes?.keys && typeof event.changes.keys.keys === 'function') {
    return event.changes.keys.keys();
  }
  return [];
}

function materializeNode(id, value) {
  const data = value && typeof value === 'object' ? value : {};
  return { id, ...data };
}

function fullNodes(yNodes) {
  return [...yNodes.entries()].map(([id, value]) => materializeNode(id, value));
}

function fullRelationships(yRelationships) {
  return [...yRelationships.values()];
}

function fullMeta(yMeta) {
  return Object.fromEntries(yMeta.entries());
}

function patchKeyedArray({ previous, keys, source, materialize }) {
  if (keys.size === 0) return previous;

  const indexByKey = new Map(previous.map((value, index) => [
    materialize.key(value),
    index,
  ]));
  const next = previous.slice();
  let structureChanged = false;

  keys.forEach((key) => {
    const index = indexByKey.get(key);
    if (!source.has(key)) {
      if (index === undefined) return;
      next[index] = null;
      structureChanged = true;
      return;
    }

    const value = materialize.value(key, source.get(key));
    if (index === undefined) {
      next.push(value);
      structureChanged = true;
      return;
    }
    next[index] = value;
  });

  return structureChanged ? next.filter(Boolean) : next;
}

function patchMeta(previous, keys, source) {
  if (keys.size === 0) return previous;
  const next = { ...previous };
  keys.forEach((key) => {
    if (source.has(key)) {
      next[key] = source.get(key);
    } else {
      delete next[key];
    }
  });
  return next;
}

/**
 * Keeps a plain React-facing projection of the three top-level Yjs maps.
 * Ordinary updates read only the keys reported by Yjs and preserve object and
 * collection identity everywhere else. Full scans remain explicit for initial
 * hydration, recovery validation, and export.
 */
export function createYjsProjection({ yNodes, yRelationships, yMeta, now = defaultNow }) {
  if (!yNodes || !yRelationships || !yMeta) {
    throw new TypeError('Yjs projection requires nodes, relationships, and meta maps');
  }

  const pending = {
    nodes: new Set(),
    relationships: new Set(),
    meta: new Set(),
  };
  let current = null;
  let revision = 0;

  const record = (scope, event) => {
    const target = pending[scope];
    if (!target) throw new TypeError(`Unknown Yjs projection scope: ${scope}`);
    for (const key of eventKeys(event)) target.add(key);
  };

  const readFullSnapshot = () => {
    const nodes = fullNodes(yNodes);
    const relationships = fullRelationships(yRelationships);
    const meta = fullMeta(yMeta);
    return {
      nodes,
      relationships,
      root: meta.root || null,
      meta,
    };
  };

  const flush = ({ full = false, updateBytes = 0 } = {}) => {
    const shouldBuildFull = full || current === null;
    const changedNodeCount = pending.nodes.size;
    const changedRelationshipCount = pending.relationships.size;
    const changedMetaCount = pending.meta.size;
    if (
      !shouldBuildFull
      && changedNodeCount === 0
      && changedRelationshipCount === 0
      && changedMetaCount === 0
      && updateBytes === 0
    ) {
      return current;
    }

    const startedAt = now();
    const fullSnapshot = shouldBuildFull ? readFullSnapshot() : null;
    const nodes = shouldBuildFull
      ? fullSnapshot.nodes
      : patchKeyedArray({
          previous: current.nodes,
          keys: pending.nodes,
          source: yNodes,
          materialize: {
            key: (node) => node.id,
            value: materializeNode,
          },
        });
    const relationships = shouldBuildFull
      ? fullSnapshot.relationships
      : patchKeyedArray({
          previous: current.relationships,
          keys: pending.relationships,
          source: yRelationships,
          materialize: {
            key: (relationship) => `${relationship?.parentId}:${relationship?.childId}`,
            value: (_key, relationship) => relationship,
          },
        });
    const meta = shouldBuildFull
      ? fullSnapshot.meta
      : patchMeta(current.meta, pending.meta, yMeta);

    pending.nodes.clear();
    pending.relationships.clear();
    pending.meta.clear();
    revision += 1;
    current = {
      nodes,
      relationships,
      root: meta.root || null,
      meta,
      projectionMetrics: {
        revision,
        mode: shouldBuildFull ? 'full' : 'incremental',
        durationMs: Math.max(0, now() - startedAt),
        updateBytes: Number.isFinite(updateBytes) ? Math.max(0, updateBytes) : 0,
        changedNodeCount: shouldBuildFull ? nodes.length : changedNodeCount,
        changedRelationshipCount: shouldBuildFull
          ? relationships.length
          : changedRelationshipCount,
        changedMetaCount: shouldBuildFull ? Object.keys(meta).length : changedMetaCount,
      },
    };
    return current;
  };

  return Object.freeze({ record, flush, readFullSnapshot, getSnapshot: () => current });
}

/** Coalesces projection work to one update per animation frame. */
export function createYjsProjectionFrameBatcher({ flush, requestFrame, cancelFrame }) {
  if (typeof flush !== 'function' || typeof requestFrame !== 'function') {
    throw new TypeError('Projection batcher requires flush and requestFrame functions');
  }

  let frameId = null;
  let fullPending = false;
  let updateBytes = 0;
  let disposed = false;
  const afterFlushCallbacks = new Set();

  const flushPending = () => {
    const options = { full: fullPending, updateBytes };
    fullPending = false;
    updateBytes = 0;
    return flush(options);
  };

  // Sync consumers can use afterFlush to publish their ready flag only after
  // this frame has materialized the document, avoiding a transient empty map.
  const schedule = ({ full = false, afterFlush = null } = {}) => {
    if (disposed) return false;
    fullPending = fullPending || full;
    if (typeof afterFlush === 'function') afterFlushCallbacks.add(afterFlush);
    if (frameId !== null) return false;
    frameId = requestFrame(() => {
      frameId = null;
      const snapshot = flushPending();
      const callbacks = [...afterFlushCallbacks];
      afterFlushCallbacks.clear();
      callbacks.forEach((callback) => callback(snapshot));
    });
    return true;
  };

  const addUpdateBytes = (bytes) => {
    if (disposed || !Number.isFinite(bytes) || bytes <= 0) return;
    updateBytes += bytes;
  };

  const dispose = () => {
    disposed = true;
    if (frameId !== null && typeof cancelFrame === 'function') cancelFrame(frameId);
    frameId = null;
    fullPending = false;
    updateBytes = 0;
    afterFlushCallbacks.clear();
  };

  return Object.freeze({ schedule, addUpdateBytes, dispose });
}
